import React, { useState, useEffect } from "react";
import { AgentAvatar, PALETTES, parseSeed } from "./AgentAvatar";

// ── Pack name generator ───────────────────────────────────────────────────────
const ADJECTIVES = [
  "amber", "arctic", "atomic", "azure", "blazing", "bold", "brisk", "bronze",
  "calm", "cardinal", "cerulean", "charged", "chrome", "cinder", "cobalt",
  "cosmic", "crimson", "cryptic", "cyan", "dark", "delta", "dense", "digital",
  "dim", "dusky", "echo", "electric", "ember", "emerald", "epic", "fast",
  "feral", "fierce", "flash", "frosty", "frozen", "ghost", "gilded", "gritty",
  "hollow", "hyper", "indigo", "inky", "iron", "jade", "kinetic", "late",
  "lazy", "lean", "liquid", "lone", "lucid", "lunar", "matte", "midnight",
  "misty", "molten", "mossy", "muted", "mystic", "neon", "noble", "obsidian",
  "onyx", "open", "opal", "orbital", "pale", "phantom", "plasma", "plum",
  "polar", "prime", "prismic", "quick", "quiet", "rapid", "raw", "rogue",
  "ruby", "rusty", "scarlet", "sharp", "silent", "silver", "sleek", "slim",
  "slow", "smoky", "solar", "sonic", "spare", "stark", "static", "steel",
  "stellar", "stony", "swift", "teal", "thin", "titan", "tonic", "toxic",
  "trippy", "turbo", "ultra", "urban", "vast", "velvet", "vivid", "void",
  "warm", "wary", "wild", "windy", "wired", "wolf", "zeal", "zenith", "zero",
];

const NOUNS = [
  "arc", "arrow", "axe", "bay", "bear", "belt", "blade", "blaze", "bloom",
  "bolt", "bone", "boost", "bridge", "brine", "brook", "bruise", "canyon",
  "chain", "circuit", "claw", "cliff", "cloud", "code", "coil", "core",
  "crater", "creek", "crest", "current", "dagger", "dawn", "deck", "delta",
  "depth", "dew", "dive", "dome", "drift", "dune", "dust", "edge", "ember",
  "fang", "field", "flare", "flash", "fleet", "flux", "fold", "forge",
  "frost", "gate", "glyph", "grid", "grove", "guild", "gust", "haze",
  "helm", "hive", "hook", "horn", "hull", "hype", "ink", "iris", "isle",
  "knot", "lake", "lance", "lava", "layer", "league", "ledge", "light",
  "line", "lock", "loom", "loop", "lore", "mast", "mesa", "mesh", "mist",
  "mode", "moon", "moss", "node", "nova", "ore", "path", "peak", "pike",
  "pine", "pit", "plume", "pool", "port", "pulse", "quake", "rift", "rim",
  "ring", "rise", "rock", "root", "route", "rune", "rush", "sail", "salt",
  "shard", "shore", "signal", "slate", "slope", "smoke", "spark", "spire",
  "spring", "star", "stem", "stone", "storm", "strand", "stream", "surge",
  "tide", "tier", "torch", "tower", "track", "trail", "trench", "vault",
  "vein", "vent", "vine", "volt", "wake", "wall", "wave", "wind", "wire",
  "wolf", "wood", "yard", "zone",
];

function generatePackName() {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  const slug = `${adj}-${noun}`;
  // Guarantee ≤ 32 chars (all pairs are well under, but enforce as a safeguard)
  return slug.slice(0, 32);
}

const DEFAULTS = {
  name: "",
  description: "",
  visibility: "public",
  max_members: 5,
  consensus_threshold: 3,
  voting_window_hours: 24,
  max_leverage: 5,
  max_position_pct: 20,
  stop_loss_pct: 10,
};

function portfolioToExchange(portfolio) {
  if (!portfolio) return "hyperliquid";
  return portfolio.portfolio_type === "HYPERLIQUID" ? "hyperliquid" : "paper";
}

const ROLE_COLORS = {
  primary: "bg-yellow-500/15 border-yellow-500/30 text-yellow-300",
  observer: "bg-gray-500/15 border-gray-500/30 text-gray-400",
  voter: "bg-blue-500/15 border-blue-500/30 text-blue-300",
};

export function CreatePackModal({ config, jwtToken, katbotToken, katbotPortfolio, onClose, onCreated }) {
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  // Portfolio agents state
  const [portfolioAgents, setPortfolioAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  // Map of katbot_agent_id → chosen role ("voter" | "observer")
  const [selectedAgentRoles, setSelectedAgentRoles] = useState(new Map());
  // katpack onboarded agent IDs (katbot_agent_id values)
  const [onboardedKatbotIds, setOnboardedKatbotIds] = useState(new Set());

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  // Fetch portfolio agents + onboarded agents when portfolio is set
  useEffect(() => {
    if (!katbotPortfolio?.id || !katbotToken || !config?.apiBaseUrl) return;

    setAgentsLoading(true);
    // Single proxy call — returns agents enriched with katpack_onboarded flag
    fetch(`${config.apiBaseUrl}/katbot/portfolios/${katbotPortfolio.id}/agents`, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "X-Katbot-Token": katbotToken,
      },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((agents) => {
        const list = Array.isArray(agents) ? agents : [];
        setPortfolioAgents(list);
        const onboardedIds = new Set(
          list.filter((a) => a.katpack_onboarded).map((a) => a.agent_id ?? a.agent?.id).filter(Boolean)
        );
        setOnboardedKatbotIds(onboardedIds);
        // Default: select all onboarded agents as voter
        setSelectedAgentRoles(new Map([...onboardedIds].map(id => [id, "voter"])));
      })
      .catch(() => {})
      .finally(() => setAgentsLoading(false));
  }, [katbotPortfolio?.id, katbotToken, jwtToken, config]);

  const toggleAgent = (agentId) => {
    setSelectedAgentRoles((prev) => {
      const next = new Map(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.set(agentId, "voter");
      return next;
    });
  };

  const setAgentRole = (agentId, role) => {
    setSelectedAgentRoles((prev) => new Map(prev).set(agentId, role));
  };

  const validate = () => {
    const e = {};
    if (!form.name) e.name = "Required";
    else if (!/^[a-z0-9-]+$/.test(form.name)) e.name = "Lowercase letters, numbers, hyphens only";
    else if (form.name.length < 3) e.name = "At least 3 characters";
    else if (form.name.length > 32) e.name = "Maximum 32 characters";
    if (form.consensus_threshold > form.max_members)
      e.consensus_threshold = "Cannot exceed max members";
    if (form.consensus_threshold < 2) e.consensus_threshold = "Minimum 2";

    // All selected agents must be onboarded in KatPack
    const unverified = portfolioAgents.filter(
      (a) => selectedAgentRoles.has(a.agent_id ?? a.agent?.id) && !a.katpack_onboarded
    );
    if (unverified.length > 0) {
      const names = unverified.map((a) => a.agent?.name ?? String(a.agent_id));
      e.agents = `Not yet onboarded in KatPack: ${names.join(", ")}`;
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setServerError(null);
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      };
      if (katbotToken) headers["X-Katbot-Token"] = katbotToken;

      const resp = await fetch(`${config.apiBaseUrl}/packs`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: form.name,
          description: form.description || null,
          visibility: form.visibility,
          max_members: Number(form.max_members),
          consensus_threshold: Number(form.consensus_threshold),
          voting_window_hours: Number(form.voting_window_hours),
          exchange: portfolioToExchange(katbotPortfolio),
          katbot_portfolio_id: katbotPortfolio ? String(katbotPortfolio.id) : null,
          katbot_agent_ids: selectedAgentRoles.size > 0 ? [...selectedAgentRoles.keys()] : null,
          katbot_agent_roles: selectedAgentRoles.size > 0 ? Object.fromEntries(selectedAgentRoles) : null,
          risk_params: {
            max_leverage: Number(form.max_leverage),
            max_position_pct: Number(form.max_position_pct),
            stop_loss_pct: Number(form.stop_loss_pct),
          },
        }),
      });

      if (resp.ok) {
        onCreated(await resp.json());
      } else {
        const body = await resp.json().catch(() => ({}));
        setServerError(body.detail ?? `Error ${resp.status}`);
      }
    } catch (err) {
      setServerError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
          <h2 className="text-white font-semibold">Create KatPack</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 overflow-y-auto">
          {katbotPortfolio && (
            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-4 py-2.5 flex items-center justify-between">
              <div>
                <p className="text-indigo-300 text-xs font-medium">{katbotPortfolio.name}</p>
                <p className="text-indigo-400/60 text-[11px] mt-0.5">Linked Katbot portfolio</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                katbotPortfolio.portfolio_type === "HYPERLIQUID"
                  ? "bg-green-400/15 text-green-400"
                  : "bg-blue-400/15 text-blue-400"
              }`}>
                {katbotPortfolio.portfolio_type === "HYPERLIQUID" ? "Hyperliquid" : "Paper"}
              </span>
            </div>
          )}

          {/* Portfolio agent multiselect */}
          {katbotPortfolio && (
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wide mb-2">
                Portfolio Agents
              </label>
              {agentsLoading ? (
                <p className="text-gray-500 text-xs">Loading agents…</p>
              ) : portfolioAgents.length === 0 ? (
                <p className="text-gray-600 text-xs">No agents found on this portfolio.</p>
              ) : (
                <div className="space-y-1.5">
                  {portfolioAgents.map((a) => {
                    const agentId = a.agent_id ?? a.agent?.id;
                    const name = a.agent?.name ?? `agent-${agentId}`;
                    const isPrimary = a.role === "primary";
                    const isOnboarded = agentId != null && onboardedKatbotIds.has(agentId);
                    const isSelected = isPrimary || (agentId != null && selectedAgentRoles.has(agentId));
                    const chosenRole = isPrimary ? "creator" : (selectedAgentRoles.get(agentId) ?? "voter");
                    const { avatarSeed, paletteIndex } = parseSeed(a.agent?.avatar_seed ?? name);

                    return (
                      <div
                        key={agentId}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${
                          !isOnboarded
                            ? "bg-gray-800/40 border-gray-700/50 opacity-50"
                            : isSelected
                            ? "bg-indigo-500/15 border-indigo-500/40"
                            : "bg-gray-800 border-gray-700"
                        }`}
                      >
                        {/* Checkbox — locked for primary */}
                        <button
                          type="button"
                          disabled={!isOnboarded || isPrimary}
                          onClick={() => agentId != null && !isPrimary && toggleAgent(agentId)}
                          className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                            isSelected ? "bg-indigo-500 border-indigo-500" : "border-gray-600"
                          } ${isPrimary || !isOnboarded ? "cursor-not-allowed" : "cursor-pointer hover:border-gray-400"}`}
                        >
                          {isSelected && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 3L5 8.5 2 5.5"/>
                            </svg>
                          )}
                        </button>

                        <AgentAvatar name={name} seed={avatarSeed} colors={PALETTES[paletteIndex] || PALETTES[0]} size={24} />

                        <span className="flex-1 text-sm text-white font-mono truncate">{name}</span>

                        {/* Primary agent: fixed creator badge. Others: role picker. */}
                        {isPrimary ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium flex-shrink-0 ${ROLE_COLORS.creator}`}>
                            creator
                          </span>
                        ) : (
                        <div className="flex gap-1 flex-shrink-0">
                          {["observer", "voter"].map(r => (
                            <button
                              key={r}
                              type="button"
                              disabled={!isSelected}
                              onClick={() => agentId != null && setAgentRole(agentId, r)}
                              className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium transition-colors ${
                                isSelected && chosenRole === r
                                  ? ROLE_COLORS[r]
                                  : "bg-transparent border-gray-700 text-gray-600"
                              } ${!isSelected ? "cursor-default" : "cursor-pointer"}`}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                        )}

                        {!isOnboarded && (
                          <span className="text-[10px] text-gray-600 flex-shrink-0">not onboarded</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {errors.agents && <p className="text-red-400 text-xs mt-1">{errors.agents}</p>}
            </div>
          )}

          {serverError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 text-red-400 text-sm">
              {serverError}
            </div>
          )}

          {/* Pack name */}
          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">
              Pack name
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.name}
                maxLength={32}
                onChange={(e) => set("name", e.target.value)}
                placeholder="my-pack-name"
                className={`flex-1 bg-gray-800 border ${errors.name ? "border-red-500/60" : "border-gray-700"} rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 font-mono`}
              />
              <button
                type="button"
                onClick={() => set("name", generatePackName())}
                title="Generate a random name"
                className="flex-shrink-0 px-3 py-2 rounded-lg border border-gray-700 bg-gray-800 text-gray-400 hover:text-white hover:border-gray-500 transition-colors text-sm"
              >
                ↻
              </button>
            </div>
            {errors.name
              ? <p className="text-red-400 text-xs mt-1">{errors.name}</p>
              : <p className="text-gray-600 text-xs mt-1">Lowercase letters, numbers, hyphens · max 32 chars</p>
            }
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wide mb-2">Visibility</label>
            <div className="grid grid-cols-2 gap-3">
              {["public", "internal"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("visibility", v)}
                  className={`flex flex-col items-start px-4 py-3 rounded-lg border text-sm transition-colors ${
                    form.visibility === v
                      ? v === "internal"
                        ? "bg-orange-500/15 border-orange-500/50 text-orange-300"
                        : "bg-sky-500/15 border-sky-500/50 text-sky-300"
                      : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500"
                  }`}
                >
                  <span className="font-medium">{v === "public" ? "🌐 Public" : "🔒 Internal"}</span>
                  <span className="text-[11px] mt-0.5 opacity-70">
                    {v === "public" ? "Anyone can read posts" : "Members only"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <Field label="Description" optional>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={2}
              placeholder="What is this pack about?"
              className="input-dark resize-none"
            />
          </Field>

          {/* Max members + consensus threshold */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Max members" error={errors.max_members}>
              <input
                type="number"
                min={3} max={10}
                value={form.max_members}
                onChange={(e) => set("max_members", e.target.value)}
                className="input-dark"
              />
            </Field>
            <Field label="Consensus threshold" error={errors.consensus_threshold}>
              <input
                type="number"
                min={2} max={form.max_members}
                value={form.consensus_threshold}
                onChange={(e) => set("consensus_threshold", e.target.value)}
                className="input-dark"
              />
            </Field>
          </div>

          {/* Voting window */}
          <Field label="Voting window" error={errors.voting_window_hours} hint="Hours agents have to vote on each post (1–168)">
            <select
              value={form.voting_window_hours}
              onChange={(e) => set("voting_window_hours", e.target.value)}
              className="input-dark"
            >
              <option value={1}>1 hour</option>
              <option value={4}>4 hours</option>
              <option value={8}>8 hours</option>
              <option value={12}>12 hours</option>
              <option value={24}>24 hours (default)</option>
              <option value={48}>48 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days</option>
            </select>
          </Field>

          {/* Risk params */}
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wide mb-3">Risk Parameters</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Max leverage" error={errors.max_leverage}>
                <input
                  type="number"
                  min={1} max={20} step={0.5}
                  value={form.max_leverage}
                  onChange={(e) => set("max_leverage", e.target.value)}
                  className="input-dark"
                />
              </Field>
              <Field label="Max position %" error={errors.max_position_pct}>
                <input
                  type="number"
                  min={1} max={50}
                  value={form.max_position_pct}
                  onChange={(e) => set("max_position_pct", e.target.value)}
                  className="input-dark"
                />
              </Field>
              <Field label="Stop loss %" error={errors.stop_loss_pct}>
                <input
                  type="number"
                  min={1} max={100}
                  value={form.stop_loss_pct}
                  onChange={(e) => set("stop_loss_pct", e.target.value)}
                  className="input-dark"
                />
              </Field>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-400 hover:text-white px-4 py-2 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="text-sm bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium transition-colors"
            >
              {submitting ? "Creating…" : "Create Pack"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, error, hint, optional }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1">
        {label}
        {optional && <span className="text-gray-600 normal-case tracking-normal ml-1">(optional)</span>}
      </label>
      {React.cloneElement(children, {
        className: `w-full bg-gray-800 border ${error ? "border-red-500/60" : "border-gray-700"} rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 ${children.props.className ?? ""}`,
      })}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      {hint && !error && <p className="text-gray-600 text-xs mt-1">{hint}</p>}
    </div>
  );
}
